// Deck statistics and the archetype guess shown in the deck builder's Deck Stats panel.
//
// The guess is arithmetic on what cards.csv says about each card (cost, type, rarity, tiers,
// arrival, abilities, and its `leans` tags). It cannot read what a card actually does, so treat it
// as a starting point.
// Everything it weighs is in ARCHETYPES below: each line is one thing the archetype likes, how much
// it matters (weight), and the range over which it counts (from "not at all" to "fully").
import { RARITIES, isEmperor, type CardDb, type CardDef } from './cards.ts';
import type { Deck } from './deck.ts';

/**
 * The four cost bands: cheap is CHEAP_MAX or less, medium runs up to MEDIUM_MAX, heavy up to HEAVY_MAX,
 * and anything above that is extra heavy.
 */
export const CHEAP_MAX = 3;
export const MEDIUM_MAX = 8;
export const HEAVY_MAX = 12;
export const COST_BANDS = [
  { id: 'cheap', name: 'Cheap', from: 0, to: CHEAP_MAX },
  { id: 'medium', name: 'Medium', from: CHEAP_MAX + 1, to: MEDIUM_MAX },
  { id: 'heavy', name: 'Heavy', from: MEDIUM_MAX + 1, to: HEAVY_MAX },
  { id: 'extra', name: 'Extra heavy', from: HEAVY_MAX + 1, to: Infinity },
] as const;
export const bandOf = (cost: number) => COST_BANDS.find((b) => cost >= b.from && cost <= b.to) ?? COST_BANDS[0];
/** Fewer cards than this and there isn't enough to guess from. */
export const MIN_CARDS_TO_GUESS = 20;

// ---------------- leanings: single cards that pull a deck toward an archetype ----------------
//
// A card leans toward an archetype in two ways:
//   - by rule, from what its row already says (the list below): nothing to type, and it follows the
//     sheet as you fill in costs, abilities and arrival;
//   - by its own `leans` cell in cards.csv, for what the rules can't see (a card that draws, one that
//     punishes attackers, one that floods the lanes with small units…).
// Each copy of a leaning card adds to that archetype's score: up to LEAN_POINTS when LEAN_FULL_AT of the
// deck leans that way.
export const LEAN_POINTS = 25;
export const LEAN_FULL_AT = 0.35;
const isUnit = (c: CardDef) => c.types.includes('troop') || c.types.includes('building');
export const LEAN_RULES: { why: string; leans: string[]; when: (card: CardDef) => boolean }[] = [
  { why: 'Ambush', leans: ['aggro', 'tempo'], when: (c) => isUnit(c) && c.arrival === 'ambush' },
  { why: 'long Entrenched arrival', leans: ['control', 'beatdown'], when: (c) => isUnit(c) && c.arrival !== 'ambush' && c.entrenchedTurns > 1 },
  { why: `cost ${MEDIUM_MAX + 1} or more`, leans: ['beatdown'], when: (c) => c.cost !== null && c.cost > MEDIUM_MAX },
  { why: 'Tap ability', leans: ['control', 'tempo'], when: (c) => c.abilities.includes('tap') },
  // (Siege: strong Buildings — sturdy, or hard-hitting. Cards that make Buildings stronger carry their own siege tag)
  { why: 'strong Building', leans: ['siege'], when: (c) => c.types.includes('building') && ((c.health ?? 0) >= 20 || (parseInt(c.damage, 10) || 0) >= 5) },
  { why: 'Tome', leans: ['mastermind'], when: (c) => c.types.includes('tome') },
  { why: 'lasts 4 turns or more', leans: ['mastermind'], when: (c) => (c.turns ?? 0) >= 4 },
  { why: 'three or more units in one card', leans: ['swarm'], when: (c) => c.types.includes('troop') && (c.count ?? 0) >= 3 },
];
const OWN_TAG = 'its own tag';
/** Every archetype a card leans toward, each with the reasons (rules that apply, or its own `leans` cell). */
export function leaningsOf(card: CardDef): Map<string, string[]> {
  const out = new Map<string, string[]>();
  // Triggers lean toward nothing
  if (card.types.includes('trigger')) return out;
  const rules = LEAN_RULES.filter((rule) => rule.when(card));
  // A card's own `leans` cell is the whole list, best fit first (1 to MAX_LEANS of them); the rules only say why a
  // tag fits ("Ambush"). A card with an empty cell (one not tagged yet) leans by the rules alone.
  if (card.leans.length) {
    for (const id of card.leans.slice(0, MAX_LEANS)) {
      const why = rules.filter((r) => r.leans.includes(id)).map((r) => r.why);
      out.set(id, why.length ? why : [OWN_TAG]);
    }
    return out;
  }
  for (const rule of rules) for (const id of rule.leans) out.set(id, [...(out.get(id) ?? []), rule.why]);
  return out;
}
/** The most archetypes one card may lean toward. */
export const MAX_LEANS = 5;

export interface DeckProfile {
  total: number; // cards in the main deck (the Emperor is left out)
  distinct: number; // different cards
  avgCopies: number;
  costed: number; // cards that have a cost
  avgCost: number;
  curve: Map<number, number>; // cost -> how many cards
  byType: Map<string, number>;
  byRarity: Map<string, number>;
  byAffiliation: Map<string, number>;
  unfinished: number; // cards whose row in cards.csv has no type yet
  // shares of the deck, 0 to 1
  low: number; // cheap
  mid: number; // medium
  high: number; // heavy and extra heavy together
  extra: number; // extra heavy alone
  troop: number;
  building: number;
  spell: number;
  trigger: number;
  tome: number;
  biome: number;
  cheapTroop: number; // cheap Troops
  heavyUnit: number; // heavy and extra heavy Troops and Buildings
  ambush: number;
  ability: number; // cards with an entrance, passive or tap ability
  tierPlay: number; // copies of cards that can tier up and are in the deck at least twice
  lowRarity: number; // common and uncommon
  rarityLevel: number; // average rarity: 0 = common … 8 = ancient
  costKnown: boolean; // at least half the deck has a cost; without that, the cost-based traits can't be judged
  lasting: number; // cards that stay in play over several turns (a turn count, or an ending of their own)
  multi: number; // cards that are several units (a Count of 2 or more) or bring summoned units
  damageSpell: number; // Spells that deal damage
  defensive: number; // cards with Shield, Buffer, Thorns or healing
  topAffiliation: { name: string; share: number }; // the affiliation most of the deck has (Neutral left out)
  topRarity: { name: string; share: number }; // the rarity most of the deck has
  leans: Record<string, number>; // archetype -> share of the deck that leans toward it
  leanWhy: Record<string, Record<string, number>>; // archetype -> reason -> how many cards lean that way for it
}

const share = (n: number, of: number) => (of > 0 ? n / of : 0);
const bump = (map: Map<string | number, number>, key: string | number, n: number) => map.set(key, (map.get(key) ?? 0) + n);

export function profileDeck(deck: Deck, db: CardDb): DeckProfile {
  const curve = new Map<number, number>();
  const byType = new Map<string, number>();
  const byRarity = new Map<string, number>();
  const byAffiliation = new Map<string, number>();
  let total = 0, distinct = 0, costed = 0, costSum = 0, low = 0, mid = 0, high = 0, extra = 0, unfinished = 0;
  let cheapTroop = 0, heavyUnit = 0, ambush = 0, ability = 0, tierPlay = 0, lowRarity = 0, raritySum = 0, rarityKnown = 0, lasting = 0;
  let multi = 0, damageSpell = 0, defensive = 0;
  const leanCount: Record<string, number> = {};
  const leanWhy: Record<string, Record<string, number>> = {};
  for (const [num, copies] of deck) {
    const card = db.byNumber.get(num);
    if (!card || isEmperor(card)) continue;
    total += copies;
    distinct++;
    for (const t of card.types) bump(byType as Map<string | number, number>, t, copies);
    bump(byRarity as Map<string | number, number>, card.rarity, copies);
    for (const a of card.affiliations) bump(byAffiliation as Map<string | number, number>, a, copies);
    if (card.types.includes('unassigned')) unfinished += copies;
    const unit = card.types.includes('troop') || card.types.includes('building');
    if (card.cost !== null) {
      costed += copies;
      costSum += card.cost * copies;
      bump(curve as Map<string | number, number>, card.cost, copies);
      if (card.cost <= CHEAP_MAX) low += copies;
      else if (card.cost > MEDIUM_MAX) high += copies;
      else mid += copies;
      if (card.cost > HEAVY_MAX) extra += copies;
      if (card.types.includes('troop') && card.cost <= CHEAP_MAX) cheapTroop += copies;
      if (unit && card.cost > MEDIUM_MAX) heavyUnit += copies;
    }
    if (unit && card.arrival === 'ambush') ambush += copies;
    if (card.abilities.length) ability += copies;
    if ((card.turns ?? 0) > 1 || card.ends.length || card.duration) lasting += copies;
    if ((card.count ?? 0) >= 2 || card.arrivesWith.length || /\bsummon/i.test(card.text)) multi += copies;
    if (card.types.includes('spell') && /\d/.test(card.damage)) damageSpell += copies;
    if (/\d/.test(card.shield + card.buffer + card.thorns + card.heals) || /\b(heal|shield|buffer|overheal|regenerat)/i.test(card.text)) defensive += copies;
    if (card.tiers > 1 && copies >= 2) tierPlay += copies;
    for (const [id, reasons] of leaningsOf(card)) {
      leanCount[id] = (leanCount[id] ?? 0) + copies; // a card counts once per archetype, however many reasons it has
      for (const why of reasons) (leanWhy[id] ??= {})[why] = (leanWhy[id][why] ?? 0) + copies;
    }
    const level = (RARITIES as readonly string[]).indexOf(card.rarity);
    if (level >= 0) {
      raritySum += level * copies;
      rarityKnown += copies;
      if (level <= 1) lowRarity += copies;
    }
  }
  const type = (t: string) => share(byType.get(t) ?? 0, total);
  const top = (map: Map<string, number>, skip: string[] = []) => {
    const best = [...map].filter(([k]) => !skip.includes(k)).sort((a, b) => b[1] - a[1])[0];
    return best ? { name: best[0], share: share(best[1], total) } : { name: '', share: 0 };
  };
  return {
    total, distinct, avgCopies: share(total, distinct), costed, avgCost: share(costSum, costed), curve, byType, byRarity, byAffiliation, unfinished,
    low: share(low, costed), mid: share(mid, costed), high: share(high, costed), extra: share(extra, costed),
    troop: type('troop'), building: type('building'), spell: type('spell'), trigger: type('trigger'), tome: type('tome'), biome: type('biome'),
    cheapTroop: share(cheapTroop, total), heavyUnit: share(heavyUnit, total), ambush: share(ambush, total), ability: share(ability, total),
    tierPlay: share(tierPlay, total), lowRarity: share(lowRarity, rarityKnown), rarityLevel: share(raritySum, rarityKnown),
    costKnown: total > 0 && costed / total >= 0.5,
    lasting: share(lasting, total),
    multi: share(multi, total),
    damageSpell: share(damageSpell, total),
    defensive: share(defensive, total),
    topAffiliation: top(byAffiliation, ['neutral']),
    topRarity: top(byRarity, ['none', 'unassigned']),
    leans: Object.fromEntries(Object.entries(leanCount).map(([id, n]) => [id, share(n, total)])),
    leanWhy,
  };
}

// ---------------- the archetypes ----------------

const pct = (x: number) => `${Math.round(x * 100)}%`;
const one = (x: number) => (Math.round(x * 10) / 10).toFixed(1);
/** 0 below `from`, 1 above `to`, a straight line between (from > to runs it the other way: less is better). */
const ramp = (x: number, from: number, to: number) => Math.max(0, Math.min(1, (x - from) / (to - from)));

interface Trait {
  cost?: boolean; // judged from Gerzite costs: left out while most of the deck has no cost yet
  weight: number; // how much this matters to the archetype; the weights of one archetype add up to 1
  value: (p: DeckProfile) => number; // 0 to 1: how well the deck shows it
  because: (p: DeckProfile) => string; // said when it is one of the reasons for the guess
}
export interface Archetype {
  id: string;
  name: string;
  plan: string; // how the archetype plays, in a line
  traits: Trait[];
}

/** How evenly the deck is spread over cheap, medium and heavy cards (1 = a third each). */
const evenness = (p: DeckProfile) => Math.max(0, 1 - 2.2 * Math.max(Math.abs(p.low - 1 / 3), Math.abs(p.mid - 1 / 3), Math.abs(p.high - 1 / 3)));
const supportShare = (p: DeckProfile) => p.spell + p.trigger + p.tome + p.biome;
const cheap = `cost ${CHEAP_MAX} or less`;
const heavy = `cost ${MEDIUM_MAX + 1} or more`;

export const ARCHETYPES: Archetype[] = [
  {
    id: 'aggro', name: 'Aggro', plan: 'Flood the lanes with cheap units and end the game before the opponent settles.',
    traits: [
      { cost: true, weight: 0.4, value: (p) => ramp(p.low, 0.35, 0.75), because: (p) => `${pct(p.low)} of the deck is cheap (${cheap})` },
      { weight: 0.3, value: (p) => ramp(p.troop, 0.45, 0.8), because: (p) => `${pct(p.troop)} of it is Troops` },
      { weight: 0.15, value: (p) => ramp(p.ambush, 0, 0.2), because: (p) => `${pct(p.ambush)} of it arrives with Ambush` },
      { cost: true, weight: 0.15, value: (p) => ramp(p.high, 0.25, 0.05), because: (p) => `only ${pct(p.high)} is heavy (${heavy})` },
    ],
  },
  {
    id: 'tempo', name: 'Tempo', plan: 'Burst out of the gate, then ride that lead for the rest of the game.',
    traits: [
      { cost: true, weight: 0.3, value: (p) => ramp(p.cheapTroop, 0.2, 0.5), because: (p) => `${pct(p.cheapTroop)} of the deck is cheap Troops for the opening turns` },
      { cost: true, weight: 0.25, value: (p) => ramp(p.mid, 0.25, 0.5), because: (p) => `${pct(p.mid)} is medium cost to keep the lead going` },
      { weight: 0.2, value: (p) => ramp(p.spell, 0.1, 0.3), because: (p) => `${pct(p.spell)} is Spells to protect it` },
      { weight: 0.15, value: (p) => ramp(p.ambush, 0, 0.15), because: (p) => `${pct(p.ambush)} arrives with Ambush` },
      { cost: true, weight: 0.1, value: (p) => ramp(p.high, 0.3, 0.1), because: (p) => `little of it (${pct(p.high)}) is heavy` },
    ],
  },
  {
    id: 'control', name: 'Control', plan: 'Keep steady pressure on from the first turn to the last.',
    traits: [
      { cost: true, weight: 0.45, value: evenness, because: (p) => `the curve covers the whole range: ${pct(p.low)} cheap, ${pct(p.mid)} medium, ${pct(p.high)} heavy` },
      { weight: 0.25, value: (p) => Math.max(0, 1 - 3 * Math.abs(p.troop + p.building - 0.5)), because: (p) => `units and everything else are in balance (${pct(p.troop + p.building)} units)` },
      { weight: 0.3, value: (p) => ramp(p.spell + p.tome, 0.15, 0.4), because: (p) => `${pct(p.spell + p.tome)} is Spells and Tomes to answer with` },
    ],
  },
  {
    id: 'beatdown', name: 'Beatdown', plan: 'Build toward one massive push of expensive cards and win in a single turn.',
    traits: [
      { cost: true, weight: 0.35, value: (p) => ramp(p.high, 0.2, 0.5), because: (p) => `${pct(p.high)} of the deck is heavy (${heavy})` },
      { cost: true, weight: 0.2, value: (p) => ramp(p.extra, 0.05, 0.25), because: (p) => `${pct(p.extra)} is extra heavy (cost ${HEAVY_MAX + 1} or more)` },
      { cost: true, weight: 0.25, value: (p) => ramp(p.heavyUnit, 0.12, 0.35), because: (p) => `${pct(p.heavyUnit)} is heavy Troops and Buildings` },
      { cost: true, weight: 0.1, value: (p) => ramp(p.avgCost, 6, 11), because: (p) => `the average cost is high (${one(p.avgCost)})` },
      { weight: 0.1, value: (p) => ramp(p.tierPlay, 0, 0.4), because: (p) => `${pct(p.tierPlay)} can be tiered up for a bigger push` },
    ],
  },
  {
    id: 'cycle', name: 'Cycle', plan: 'Play cheap cards quickly to turn the deck over and reach the key ones again.',
    traits: [
      { cost: true, weight: 0.45, value: (p) => ramp(p.low, 0.5, 0.85), because: (p) => `${pct(p.low)} of the deck is cheap (${cheap})` },
      { cost: true, weight: 0.25, value: (p) => ramp(p.avgCost, 6, 3), because: (p) => `the average cost is low (${one(p.avgCost)})` },
      { weight: 0.15, value: (p) => ramp(p.avgCopies, 3, 7), because: (p) => `it runs many copies of few cards (${one(p.avgCopies)} of each)` },
      { cost: true, weight: 0.15, value: (p) => ramp(p.rarityLevel, 1, 3.5), because: () => 'the cheap cards are still fairly rare ones' },
    ],
  },
  {
    id: 'bait', name: 'Bait', plan: 'Throw out a stream of cheap, common cards to draw answers, then land what matters.',
    traits: [
      { cost: true, weight: 0.4, value: (p) => ramp(p.low, 0.5, 0.85), because: (p) => `${pct(p.low)} of the deck is cheap (${cheap})` },
      { weight: 0.35, value: (p) => ramp(p.lowRarity, 0.5, 0.9), because: (p) => `${pct(p.lowRarity)} is Common or Uncommon` },
      { cost: true, weight: 0.25, value: (p) => ramp(p.avgCost, 5, 2.5), because: (p) => `the average cost is very low (${one(p.avgCost)})` },
    ],
  },
  {
    id: 'combo', name: 'Combo', plan: 'Assemble pieces that are far stronger together than apart.',
    traits: [
      { weight: 0.4, value: (p) => ramp(p.tierPlay, 0.15, 0.6), because: (p) => `${pct(p.tierPlay)} of the deck is copies that tier up together` },
      { weight: 0.3, value: (p) => ramp(p.ability, 0.15, 0.6), because: (p) => `${pct(p.ability)} has an Entrance, Passive or Tap ability to chain` },
      { weight: 0.15, value: (p) => ramp(p.avgCopies, 3, 8), because: (p) => `it leans on many copies of few cards (${one(p.avgCopies)} of each)` },
      { weight: 0.15, value: (p) => ramp(p.tome, 0, 0.1), because: (p) => `${pct(p.tome)} is Tomes to set things up` },
    ],
  },
  {
    id: 'siege', name: 'Siege', plan: 'Hold the lanes with strong Buildings, back them with cards that make them stronger, and grind the opponent down from behind them.',
    traits: [
      { weight: 0.8, value: (p) => ramp(p.building, 0.1, 0.4), because: (p) => `${pct(p.building)} of the deck is Buildings` },
      { cost: true, weight: 0.2, value: (p) => ramp(p.mid + p.high, 0.4, 0.8), because: (p) => `${pct(p.mid + p.high)} costs more than ${CHEAP_MAX}, for a slower game` },
    ],
  },
  {
    id: 'support', name: 'Support', plan: 'Win through Spells, Biomes and Support cards more than through units.',
    traits: [
      { weight: 0.8, value: (p) => ramp(supportShare(p), 0.3, 0.65), because: (p) => `${pct(supportShare(p))} of the deck is Spells, Biomes, Tomes and Triggers` },
      { weight: 0.2, value: (p) => ramp(p.biome + p.tome, 0.03, 0.15), because: (p) => `${pct(p.biome + p.tome)} is Biomes and Tomes` },
    ],
  },
  {
    id: 'counter', name: 'Counter / Punish', plan: 'Wait for the opponent to commit, then make them pay for it.',
    traits: [
      { weight: 0.6, value: (p) => ramp(p.trigger, 0.06, 0.25), because: (p) => `${pct(p.trigger)} of the deck is Triggers, which punish what the opponent does` },
      { weight: 0.25, value: (p) => ramp(p.spell, 0.1, 0.35), because: (p) => `${pct(p.spell)} is Spells to answer with` },
      { weight: 0.15, value: (p) => ramp(p.troop + p.building, 0.8, 0.5), because: (p) => `it runs few units (${pct(p.troop + p.building)})` },
    ],
  },
];

ARCHETYPES.push(
  {
    id: 'synergy', name: 'Synergy', plan: 'Pair up particular cards that work off each other and are far stronger together than apart.',
    traits: [
      { weight: 0.5, value: (p) => ramp(p.ability, 0.2, 0.6), because: (p) => `${pct(p.ability)} of the deck has an Entrance, Passive or Tap ability to play off` },
      { weight: 0.25, value: (p) => ramp(p.avgCopies, 2.5, 6), because: (p) => `it runs several copies of its pieces (${one(p.avgCopies)} of each)` },
      { weight: 0.25, value: (p) => ramp(p.spell + p.tome, 0.1, 0.3), because: (p) => `${pct(p.spell + p.tome)} is Spells and Tomes to tie them together` },
    ],
  },
  {
    id: 'brainrot', name: 'Brainrot', plan: 'Disrupt and annoy: effects that throw the opponent off their game and make every turn awkward for them.',
    traits: [
      { weight: 0.35, value: (p) => ramp(p.trigger, 0.04, 0.2), because: (p) => `${pct(p.trigger)} of the deck is Triggers that go off on the opponent` },
      { weight: 0.25, value: (p) => ramp(p.spell, 0.1, 0.35), because: (p) => `${pct(p.spell)} is Spells to meddle with` },
      { weight: 0.2, value: (p) => ramp(p.ability, 0.2, 0.6), because: (p) => `${pct(p.ability)} has an Entrance, Passive or Tap ability` },
      { weight: 0.2, value: (p) => ramp(p.biome, 0, 0.08), because: (p) => `${pct(p.biome)} is Biomes that change the field for everyone` },
    ],
  },
  {
    id: 'mastermind', name: 'Mastermind', plan: 'Set up long-term effects that shape the board turns ahead, building toward a checkmate.',
    traits: [
      { weight: 0.4, value: (p) => ramp(p.lasting, 0.08, 0.3), because: (p) => `${pct(p.lasting)} of the deck stays in play over several turns` },
      { weight: 0.3, value: (p) => ramp(p.tome + p.biome, 0.03, 0.15), because: (p) => `${pct(p.tome + p.biome)} is Tomes and Biomes` },
      { cost: true, weight: 0.15, value: (p) => ramp(p.mid + p.high, 0.4, 0.8), because: (p) => `${pct(p.mid + p.high)} costs more than ${CHEAP_MAX}, for a longer game` },
      { weight: 0.15, value: (p) => ramp(p.ability, 0.2, 0.6), because: (p) => `${pct(p.ability)} has an Entrance, Passive or Tap ability` },
    ],
  },
  {
    id: 'typecast', name: 'Typecast', plan: 'Stick to cards that work best alongside their own affiliation or rarity.',
    traits: [
      { weight: 0.6, value: (p) => ramp(p.topAffiliation.share, 0.4, 0.85), because: (p) => `${pct(p.topAffiliation.share)} of the deck is ${p.topAffiliation.name ? p.topAffiliation.name[0].toUpperCase() + p.topAffiliation.name.slice(1) : 'one affiliation'}` },
      { weight: 0.4, value: (p) => ramp(p.topRarity.share, 0.4, 0.75), because: (p) => `${pct(p.topRarity.share)} of it is ${p.topRarity.name ? p.topRarity.name.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'one rarity'}` },
    ],
  },
);

// The archetypes below are mostly about what cards SAY (milling, locking the opponent down, sacrificing…),
// which the table can't read: they go by the cards that lean toward them (their `leans` cell in cards.csv),
// helped by what the profile can see.
const leaning = (id: string, from = 0.05, to = 0.3) => (p: DeckProfile) => ramp(p.leans[id] ?? 0, from, to);
const leanSays = (id: string, what: string) => (p: DeckProfile) => `${pct(p.leans[id] ?? 0)} of the deck ${what}`;
ARCHETYPES.push(
  {
    id: 'midrange', name: 'Midrange', plan: 'Balances offense and defense with efficient cards, adapting its strategy depending on the opponent.',
    traits: [
      { cost: true, weight: 0.4, value: (p) => ramp(p.mid, 0.35, 0.6), because: (p) => `${pct(p.mid)} of the deck is medium cost` },
      { weight: 0.25, value: (p) => Math.max(0, 1 - 3 * Math.abs(p.troop + p.building - 0.6)), because: (p) => `it runs a solid core of units (${pct(p.troop + p.building)}) with answers beside them` },
      { cost: true, weight: 0.2, value: (p) => Math.min(ramp(p.low, 0.6, 0.25), ramp(p.high, 0.4, 0.15)), because: (p) => `few extremes: ${pct(p.low)} cheap, ${pct(p.high)} heavy` },
      { weight: 0.15, value: leaning('midrange'), because: leanSays('midrange', 'is efficient all-rounders') },
    ],
  },
  {
    id: 'ramp', name: 'Ramp', plan: 'Accelerates resource generation to play expensive, powerful cards earlier than normal.',
    traits: [
      { weight: 0.5, value: leaning('ramp', 0.04, 0.2), because: leanSays('ramp', 'gains Gerzite or makes cards cheaper') },
      { cost: true, weight: 0.35, value: (p) => ramp(p.high, 0.15, 0.4), because: (p) => `${pct(p.high)} of it is heavy (${heavy}): the payoff` },
      { cost: true, weight: 0.15, value: (p) => ramp(p.extra, 0.03, 0.15), because: (p) => `${pct(p.extra)} is extra heavy` },
    ],
  },
  {
    id: 'swarm', name: 'Swarm', plan: 'Overwhelms opponents through sheer numbers of cheap troops or summoned units.',
    traits: [
      { weight: 0.35, value: (p) => ramp(p.multi, 0.1, 0.4), because: (p) => `${pct(p.multi)} of the deck is several units in one card or summons more` },
      { cost: true, weight: 0.3, value: (p) => ramp(p.cheapTroop, 0.2, 0.5), because: (p) => `${pct(p.cheapTroop)} is cheap Troops` },
      { weight: 0.2, value: leaning('swarm'), because: leanSays('swarm', 'leans toward going wide') },
      { weight: 0.15, value: (p) => ramp(p.troop, 0.5, 0.8), because: (p) => `${pct(p.troop)} is Troops` },
    ],
  },
  {
    id: 'direct_attack', name: 'Direct Attack', plan: 'Focuses on direct damage through spells and abilities rather than conventional combat.',
    traits: [
      { weight: 0.4, value: (p) => ramp(p.damageSpell, 0.08, 0.3), because: (p) => `${pct(p.damageSpell)} of the deck is damage Spells` },
      { weight: 0.35, value: leaning('direct_attack'), because: leanSays('direct_attack', 'deals damage outside of combat') },
      { weight: 0.25, value: (p) => ramp(p.spell, 0.15, 0.4), because: (p) => `${pct(p.spell)} is Spells` },
    ],
  },
  {
    id: 'mill', name: 'Mill', plan: 'Forces opponents to discard or exhaust cards from their deck, winning through deck depletion or fatigue.',
    traits: [
      { weight: 0.85, value: leaning('mill', 0.04, 0.2), because: leanSays('mill', 'eats away at the opponent’s deck') },
      { weight: 0.15, value: (p) => ramp(p.spell + p.trigger, 0.15, 0.4), because: (p) => `${pct(p.spell + p.trigger)} is Spells and Triggers to do it with` },
    ],
  },
  {
    id: 'stall', name: 'Stall', plan: 'Delays the game through healing, shields, defensive troops, and other survival tools until a win condition is reached.',
    traits: [
      { weight: 0.4, value: (p) => ramp(p.defensive, 0.1, 0.4), because: (p) => `${pct(p.defensive)} of the deck heals, shields or bites back` },
      { weight: 0.4, value: leaning('stall'), because: leanSays('stall', 'is built to survive') },
      { weight: 0.2, value: (p) => ramp(p.building, 0.05, 0.25), because: (p) => `${pct(p.building)} is Buildings holding the lanes` },
    ],
  },
  {
    id: 'prison', name: 'Prison', plan: 'Restricts the opponent’s available actions, preventing them from playing their cards effectively.',
    traits: [
      { weight: 0.75, value: leaning('prison', 0.04, 0.25), because: leanSays('prison', 'locks down what the opponent can do') },
      { weight: 0.25, value: (p) => ramp(p.trigger + p.biome + p.tome, 0.05, 0.25), because: (p) => `${pct(p.trigger + p.biome + p.tome)} is Triggers, Biomes and Tomes that hold the board` },
    ],
  },
  {
    id: 'sacrifice', name: 'Sacrifice', plan: 'Deliberately destroys or sacrifices friendly troops to trigger beneficial effects.',
    traits: [
      { weight: 0.8, value: leaning('sacrifice', 0.04, 0.2), because: leanSays('sacrifice', 'gains from its own units going') },
      { cost: true, weight: 0.2, value: (p) => ramp(p.cheapTroop, 0.15, 0.4), because: (p) => `${pct(p.cheapTroop)} is cheap Troops to give up` },
    ],
  },
  {
    id: 'voltron', name: 'Voltron', plan: 'Concentrates buffs, equipment, and protection on a single powerful troop.',
    traits: [
      { weight: 0.6, value: leaning('voltron', 0.04, 0.2), because: leanSays('voltron', 'buffs or protects a single unit') },
      { weight: 0.2, value: (p) => ramp(p.tierPlay, 0.1, 0.4), because: (p) => `${pct(p.tierPlay)} tiers up into one bigger unit` },
      { weight: 0.2, value: (p) => ramp(p.troop + p.building, 0.6, 0.3), because: (p) => `only ${pct(p.troop + p.building)} is units: few, but well looked after` },
    ],
  },
  {
    id: 'toolbox', name: 'Toolbox', plan: 'Includes specialized cards for different situations, often using search effects to retrieve the right solution.',
    traits: [
      { weight: 0.55, value: leaning('toolbox', 0.04, 0.2), because: leanSays('toolbox', 'searches the deck or answers a special case') },
      { weight: 0.45, value: (p) => ramp(p.avgCopies, 3, 1.4), because: (p) => `many different cards, few copies of each (${one(p.avgCopies)} on average)` },
    ],
  },
  {
    id: 'disruption', name: 'Disruption', plan: 'Attacks the opponent’s strategy through discarding, resource destruction, disabling, or interference.',
    traits: [
      { weight: 0.7, value: leaning('disruption', 0.04, 0.25), because: leanSays('disruption', 'interferes with the opponent’s hand, Gerzite or cards') },
      { weight: 0.15, value: (p) => ramp(p.trigger, 0.04, 0.2), because: (p) => `${pct(p.trigger)} is Triggers` },
      { weight: 0.15, value: (p) => ramp(p.spell, 0.1, 0.35), because: (p) => `${pct(p.spell)} is Spells` },
    ],
  },
  {
    id: 'token', name: 'Token', plan: 'Generates expendable creatures or other temporary cards to create board advantage. Often overlaps with Swarm.',
    traits: [
      { weight: 0.6, value: leaning('token', 0.04, 0.25), because: leanSays('token', 'makes summoned units or other temporary cards') },
      { weight: 0.4, value: (p) => ramp(p.multi, 0.08, 0.35), because: (p) => `${pct(p.multi)} brings extra units onto the table` },
    ],
  },
  {
    id: 'alt_win', name: 'Alternate Win', plan: 'Pursues an unconventional victory condition beyond reducing the opponent’s health to zero.',
    traits: [{ weight: 1, value: leaning('alt_win', 0.02, 0.1), because: leanSays('alt_win', 'works toward another way to win') }],
  },
);

export interface ArchetypeFit {
  id: string;
  name: string;
  plan: string;
  fit: number; // 0 to 100: how well the deck matches (not a probability; several can be high at once)
  reasons: string[]; // the traits that count most toward it, strongest first
}

/** "Ambush, a Tap ability and its own tag": the commonest reasons cards lean toward an archetype. */
function leanReasons(why: Record<string, number> = {}) {
  const top = Object.entries(why).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([w]) => w);
  return top.length > 1 ? `${top.slice(0, -1).join(', ')} and ${top.at(-1)}` : (top[0] ?? '');
}

/** Every archetype scored against a deck, best match first. Empty if the deck is too small to tell. */
export function guessArchetypes(p: DeckProfile): ArchetypeFit[] {
  if (p.total < MIN_CARDS_TO_GUESS) return [];
  return ARCHETYPES.map((a) => {
    // a trait judged from costs counts for nothing while most of the deck has no cost yet
    const scored = a.traits.map((t) => {
      const value = t.cost && !p.costKnown ? 0 : t.value(p);
      return { part: t.weight * value, value, because: t.because(p) };
    });
    // cards that lean toward this archetype add to it on top
    const lean = p.leans[a.id] ?? 0;
    if (lean > 0) {
      const value = ramp(lean, 0, LEAN_FULL_AT);
      scored.push({ part: (LEAN_POINTS / 100) * value, value: lean >= 0.05 ? 1 : 0, because: `${pct(lean)} of the deck leans this way (${leanReasons(p.leanWhy[a.id])})` });
    }
    const fit = Math.min(100, Math.round(100 * scored.reduce((sum, s) => sum + s.part, 0)));
    const reasons = scored.filter((s) => s.value >= 0.35 && s.part > 0).sort((x, y) => y.part - x.part).slice(0, 3).map((s) => s.because);
    return { id: a.id, name: a.name, plan: a.plan, fit, reasons };
  }).sort((x, y) => y.fit - x.fit);
}
